import React, { forwardRef, useRef, useState, useCallback, useEffect } from 'react';

import { findLastIndex, split } from 'lodash';
import { DateTime } from 'luxon';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { FilterDefinition, FilterValue } from 'common/filters/Filters.types';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { DatePickerWithLocale } from 'core/datepicker/DatePicker';
import { CUSTOM_DATE_DELIMITER, CUSTOM_DATE_TIME_FORMAT, DateRange, Period } from 'core/util/DateRange';

import 'common/filters/filter-bar/custom-date-filter/CustomDateFilter.scss';

export const toDateRange = (dateString: string): DateRange | undefined => {
    const dates = split(dateString, CUSTOM_DATE_DELIMITER);

    if (dates.length === 2) {
        const startDate = DateTime.fromFormat(dates[0], CUSTOM_DATE_TIME_FORMAT);
        const endDate = DateTime.fromFormat(dates[1], CUSTOM_DATE_TIME_FORMAT);

        if (startDate.isValid && endDate.isValid) {
            return {
                startDate: startDate.toJSDate(),
                endDate: endDate.toJSDate(),
                label: toLabel(startDate, endDate),
                value: toValue(startDate, endDate),
            };
        }
    }

    return undefined;
};

const toLabel = (startDate: DateTime, endDate: DateTime): string => {
    return `${startDate.toLocaleString(DateTime.DATE_MED)} - ${endDate.toLocaleString(DateTime.DATE_MED)}`;
};

const toValue = (startDate: DateTime, endDate: DateTime): string => {
    return `${startDate.toFormat(CUSTOM_DATE_TIME_FORMAT)}${CUSTOM_DATE_DELIMITER}${endDate.toFormat(
        CUSTOM_DATE_TIME_FORMAT,
    )}`;
};

interface Props {
    filter: FilterDefinition;
    onCancel: () => void;
    onChange: (filterValue: FilterValue) => void;
}

export const CustomDateFilter = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const { t } = useTranslation(['common/filters']);
    const [, dispatch] = useMessage();

    const ONE_MONTH_AGO = DateTime.local().minus({ months: 1 }).toJSDate();
    const TODAY = DateTime.local().toJSDate();

    const filterValueIndex = findLastIndex(props.filter.values, (filterValue) => filterValue.value === Period.Custom);
    const initialDateRange = props.filter.values[filterValueIndex].dateRange;

    const [startDate, setStartDate] = useState(initialDateRange ? initialDateRange.startDate : ONE_MONTH_AGO);
    const [endDate, setEndDate] = useState(initialDateRange ? initialDateRange.endDate : TODAY);
    const [validated, setValidated] = useState(false);
    const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
    const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);
    const [activeField, setActiveField] = useState<'from' | 'to'>('from');

    const menuRef = useRef<HTMLDivElement | null>(null);
    const popperHostRef = useRef<HTMLDivElement | null>(null);

    const getFocusableElements = useCallback((): HTMLElement[] => {
        if (!menuRef.current) return [];

        return Array.from(
            menuRef.current.querySelectorAll<HTMLElement>('input:not([disabled]), button:not([disabled])'),
        ).filter((el) => el.offsetParent !== null);
    }, []);

    const setCombinedRef = useCallback(
        (node: HTMLDivElement | null) => {
            menuRef.current = node;

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                const objectRef = ref as React.MutableRefObject<HTMLDivElement | null>;

                objectRef.current = node;
            }
        },
        [ref],
    );

    useEffect(() => {
        const focusTimer = window.setTimeout(() => {
            const focusable = getFocusableElements();

            focusable[0]?.focus();
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [getFocusableElements]);

    const onApply = (event: React.MouseEvent<HTMLButtonElement>): boolean => {
        event.preventDefault();

        startDate.setHours(0, 0, 0);
        endDate.setHours(23, 59, 59);

        const startDateDT = DateTime.fromJSDate(startDate);
        const endDateDT = DateTime.fromJSDate(endDate);
        const dateRange = {
            startDate,
            endDate,
            label: toLabel(startDateDT, endDateDT),
            value: toValue(startDateDT, endDateDT),
        };
        const { months } = endDateDT.diff(startDateDT, 'months').toObject();
        const { dateWindowInMonths } = props.filter?.extraOptions || {};

        if (months && months > dateWindowInMonths) {
            dispatch({
                type: MessageActionType.ShowError,
                text: t('SELECTED_DATES_OUT_OF_RANGE', { months: dateWindowInMonths }),
            });
        } else {
            // eslint-disable-next-line no-param-reassign
            props.filter.values[filterValueIndex].dateRange = dateRange;
            props.onChange(props.filter.values[filterValueIndex]);
        }

        setValidated(true);

        return false;
    };

    const focusElementAtIndex = useCallback(
        (index: number): void => {
            window.setTimeout(() => {
                const focusable = getFocusableElements();

                focusable[index]?.focus();
            }, 0);
        },
        [getFocusableElements],
    );

    const handleMenuKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        const target = e.target as HTMLElement;
        const focusable = getFocusableElements();
        // const currentIndex = focusable.indexOf(target);

        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setIsStartPickerOpen(false);
            setIsEndPickerOpen(false);
            props.onCancel();
            return;
        }
        if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();

            const fromInput = focusable[0];
            const toInput = focusable[1];
            const cancelButton = focusable[2];
            const applyButton = focusable[3];

            if (isStartPickerOpen) {
                setIsStartPickerOpen(false);
                (e.shiftKey ? fromInput : toInput)?.focus();
                return;
            }

            if (isEndPickerOpen) {
                setIsEndPickerOpen(false);
                (e.shiftKey ? toInput : cancelButton)?.focus();
                return;
            }

            if (target === fromInput) {
                (e.shiftKey ? fromInput : toInput)?.focus();
                return;
            }

            if (target === toInput) {
                (e.shiftKey ? fromInput : cancelButton)?.focus();
                return;
            }

            if (target === cancelButton) {
                (e.shiftKey ? toInput : applyButton)?.focus();
                return;
            }

            if (target === applyButton) {
                (e.shiftKey ? cancelButton : applyButton)?.focus();
                return;
            }

            return;
        }

        if (e.key === 'Enter') {
            const startInput = focusable[0];
            const endInput = focusable[1];

            if (target === startInput) {
                e.preventDefault();
                e.stopPropagation();
                setIsStartPickerOpen(true);
                setIsEndPickerOpen(false);
                return;
            }

            if (target === endInput) {
                e.preventDefault();
                e.stopPropagation();
                setIsEndPickerOpen(true);
                setIsStartPickerOpen(false);
            }
        }
    };

    const focusCalendarDay = useCallback((field: 'from' | 'to'): void => {
        requestAnimationFrame(() => {
            window.setTimeout(() => {
                const calendarRoot = document.querySelector<HTMLElement>(`.custom-date-calendar--${field}`);

                const dayToFocus =
                    calendarRoot?.querySelector<HTMLElement>('.react-datepicker__day--keyboard-selected') ||
                    calendarRoot?.querySelector<HTMLElement>('.react-datepicker__day--selected') ||
                    calendarRoot?.querySelector<HTMLElement>('.react-datepicker__day[tabindex="0"]');

                dayToFocus?.focus();
            }, 0);
        });
    }, []);

    useEffect(() => {
        if (isStartPickerOpen) {
            focusCalendarDay('from');
        }
    }, [isStartPickerOpen, focusCalendarDay]);

    useEffect(() => {
        if (isEndPickerOpen) {
            focusCalendarDay('to');
        }
    }, [isEndPickerOpen, focusCalendarDay]);

    // TODO - date inputs do not reflect previousValue on cancel

    return (
        <Dropdown.Menu
            className="r4e-custom-date-filter"
            data-custom-date-filter="true"
            data-attr-name={props.filter.name}
            ref={setCombinedRef}
            role="group"
            aria-label={t('CUSTOM_DATE')}
            onKeyDown={handleMenuKeyDownCapture}
        >
            {props.children}
            <div ref={popperHostRef} className="custom-date-filter__popper-host" />
            <Form noValidate validated={validated}>
                <Form.Group className={!startDate ? 'invalid' : ''}>
                    <Form.Label>{t('CUSTOM_DATE_FROM')}</Form.Label>
                    <div
                        onMouseDown={(e): void => e.stopPropagation()}
                        onClick={(e): void => e.stopPropagation()}
                        role="presentation"
                    >
                        <DatePickerWithLocale
                            calendarClassName="custom-date-calendar--from"
                            popperContainer={({ children }: { children: React.ReactNode }) => (
                                <div className="custom-date-filter__popper">{children}</div>
                            )}
                            placeholderText={t('CUSTOM_DATE_FROM')}
                            selected={startDate}
                            maxDate={endDate}
                            onChange={(date: Date): void => {
                                setStartDate(date);
                            }}
                            required
                            preventOpenOnFocus
                            open={isStartPickerOpen}
                            onInputClick={(): void => {
                                setActiveField('from');
                                setIsStartPickerOpen(true);
                                setIsEndPickerOpen(false);
                            }}
                            onFocus={(): void => {
                                setActiveField('from');
                            }}
                            onClickOutside={(): void => {
                                setIsStartPickerOpen(false);
                                focusElementAtIndex(0);
                            }}
                            onCalendarClose={(): void => {
                                if (activeField === 'from') {
                                    focusElementAtIndex(0);
                                }
                            }}
                            onSelect={(): void => {
                                setIsStartPickerOpen(false);
                                focusElementAtIndex(1);
                            }}
                        />
                    </div>
                    <Form.Control.Feedback type="invalid">{t('VALIDATION_DATE_REQUIRED')}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className={!endDate ? 'invalid' : ''}>
                    <Form.Label>{t('CUSTOM_DATE_TO')}</Form.Label>
                    <div
                        onMouseDown={(e): void => e.stopPropagation()}
                        onClick={(e): void => e.stopPropagation()}
                        role="presentation"
                    >
                        <DatePickerWithLocale
                            calendarClassName="custom-date-calendar--to"
                            popperContainer={({ children }: { children: React.ReactNode }) => (
                                <div className="custom-date-filter__popper">{children}</div>
                            )}
                            placeholderText={t('CUSTOM_DATE_TO')}
                            selected={endDate}
                            minDate={startDate}
                            maxDate={TODAY}
                            onChange={(date: Date): void => {
                                setEndDate(date);
                            }}
                            required
                            preventOpenOnFocus
                            open={isEndPickerOpen}
                            onInputClick={(): void => {
                                setActiveField('to');
                                setIsEndPickerOpen(true);
                                setIsStartPickerOpen(false);
                            }}
                            onFocus={(): void => {
                                setActiveField('to');
                            }}
                            onClickOutside={(): void => {
                                setIsEndPickerOpen(false);
                                focusElementAtIndex(1);
                            }}
                            onCalendarClose={(): void => {
                                if (activeField === 'to') {
                                    focusElementAtIndex(1);
                                }
                            }}
                            onSelect={(): void => {
                                setIsEndPickerOpen(false);
                                focusElementAtIndex(1);
                            }}
                        />
                    </div>
                    <Form.Control.Feedback type="invalid">{t('VALIDATION_DATE_REQUIRED')}</Form.Control.Feedback>
                </Form.Group>
                <div className="text-right">
                    <Button
                        variant="link"
                        type="button"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            props.onCancel();
                        }}
                    >
                        {t('CUSTOM_DATE_CANCEL')}
                    </Button>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onApply(e as React.MouseEvent<HTMLButtonElement>);
                        }}
                    >
                        {t('CUSTOM_DATE_APPLY')}
                    </Button>
                </div>
            </Form>
        </Dropdown.Menu>
    );
});
